import { InputWithLabel } from '@adotel/components'
import { Typography } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'

import { getTranslate as t } from '../../localization'
import useStyles from './styles/index.styles'

/**
 * The single-text-search filter the portal already uses on the ticket lists:
 * a full-width input with a leading search icon, filtering client-side.
 *
 * Shared rather than copied because two panels now need it — ResourcePanel
 * (six management tabs) and DeliveryList (Deliveries and the personal
 * History). One copy was a judgement call; a second is the point at which the
 * wording, the live region and the "loaded" distinction below would start
 * drifting apart between them.
 */
export function ListFilter({
  label,
  value,
  onChange,
  shown,
  loaded
}: {
  /** What is being searched, e.g. "Categories". Lowercased for the placeholder. */
  label: string
  value: string
  onChange: (next: string) => void
  shown: number
  loaded: number
}) {
  const classes = useStyles()

  return (
    <div className={classes.filterRow}>
      <InputWithLabel
        data-cy="resourceSearchInput"
        aria-label={t('Filter {title}', {
          title: t(label).toLowerCase()
        })}
        placeholder={t('Search {title}', { title: t(label).toLowerCase() })}
        autoComplete="off"
        noHelperText
        fullWidth
        inputSize="small"
        startIcon={<SearchIcon />}
        value={value}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          onChange(event.currentTarget.value)
        }
      />
      {/* A live region: filtering removes rows while focus stays in the input,
          with no other feedback. aria-atomic so the whole phrase is read, not
          just the digit that changed.

          One key with placeholders rather than concatenated words — Farsi
          orders these differently and a translator cannot reorder fragments.

          Says "loaded", never "total": the server has not told us how many
          exist, so a total would be a claim we cannot make.

          Named because a host panel may carry a second live region —
          ResourcePanel's action result. Unnamed, they announce identically and
          a listener cannot tell which one just changed. */}
      <Typography
        variant="caption"
        className={classes.filterCount}
        role="status"
        aria-label={t('Filter results')}
        aria-live="polite"
        aria-atomic="true">
        {t('{shown} of {loaded} loaded', { shown, loaded })}
      </Typography>
    </div>
  )
}
